import { useQuery, useQueryClient } from "@tanstack/react-query";
import { explorerService } from "/app/src/services/explorerService";
import { ExplorerCounts } from "/app/src/types/explorer";

type Counts = {
  ordersCount: number | string;
  orderLinesCount: number | string;
  transactionsCount: number | string;
  batchesCount: number | string;
  materialsCount: number | string;
  materialPropertiesCount: number | string;
  locationsCount: number | string;
  warehousesCount: number | string;
  zonesCount: number | string;
  binsCount: number | string;
  costCentersCount: number | string;
  parameterValuesCount: number | string;
};

/**
 * Retrieves counts for various models using react-query's useQueries hook,
 * formatting large numbers with k/M suffixes.
 *
 * @returns {Counts} An object mapping model names to their formatted counts.
 */
export default function useModelCounts(): Counts {
  /**
   * Format a numeric count into a shorter label when large.
   * - >= 1,000,000 => `x.xM`
   * - >= 10,000 => `x.xk`
   * - otherwise => the raw number
   */
  const formatCount = (count: number | undefined | null): number | string => {
    if (typeof count !== "number") return 0;
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 10000) return `${(count / 1000).toFixed(1)}k`;
    return count;
  };

  const queryClient = useQueryClient();

  // Map each per-count query key to the corresponding field returned by the
  // explorer counts endpoint. Use `keyof ExplorerCounts` to avoid `any`.
  const queryKeyMap: Array<{ key: unknown[]; field: keyof ExplorerCounts }> = [
    {
      key: ["orders", "count", "", { priority: "all", state: "queued" }],
      field: "orderCount",
    },
    {
      key: ["order_lines", "count", "", { state: "queued" }],
      field: "orderLineCount",
    },
    {
      key: ["transactions", "count", "", { type: "[or]all" }],
      field: "transactionCount",
    },
    { key: ["batches", "count", "", {}], field: "batchCount" },
    { key: ["materials", "count", "", {}], field: "materialCount" },
    {
      key: ["material_properties", "count", "", {}],
      field: "materialPropertyCount",
    },
    {
      key: ["locations", "count", "", { isMarkedFull: "all", zoneId: "all" }],
      field: "locationCount",
    },
    { key: ["warehouses", "count", "", {}], field: "warehouseCount" },
    { key: ["zones", "count", "", {}], field: "zoneCount" },
    { key: ["bins", "count", "", {}], field: "binCount" },
    { key: ["cost_centers", "count", "", {}], field: "costCenterCount" },
    { key: ["parameter_values", "count", "", {}], field: "parameterCount" },
  ];

  // Provide an explicit zero-filled fallback that matches ExplorerCounts so we
  // can use it as `initialData` and avoid `undefined`/`any` pain.
  const emptyCounts: ExplorerCounts = {
    orderCount: 0,
    orderLineCount: 0,
    transactionCount: 0,
    batchCount: 0,
    materialCount: 0,
    materialPropertyCount: 0,
    locationCount: 0,
    warehouseCount: 0,
    zoneCount: 0,
    costCenterCount: 0,
    binCount: 0,
    parameterCount: 0,
  };

  // Fetch all counts in one request and populate individual query entries
  const { data: allCounts = emptyCounts } = useQuery<ExplorerCounts, Error>(
    ["explorer", "counts"],
    () => explorerService.getCounts(),
    {
      initialData: emptyCounts,
      onSuccess: (data: ExplorerCounts) => {
        // Populate individual query caches with the raw `{ count: number }` shape
        queryKeyMap.forEach(({ key, field }) => {
          const value = data[field] ?? 0;
          // setQueryData accepts unknown; providing the same shape other
          // consumers expect keeps backwards-compatibility.
          queryClient.setQueryData(key, { count: value });
        });
      },
    },
  );

  const ordersCount = formatCount(allCounts.orderCount);
  const orderLinesCount = formatCount(allCounts.orderLineCount);
  const transactionsCount = formatCount(allCounts.transactionCount);
  const batchesCount = formatCount(allCounts.batchCount);
  const materialsCount = formatCount(allCounts.materialCount);
  const materialPropertiesCount = formatCount(allCounts.materialPropertyCount);
  const locationsCount = formatCount(allCounts.locationCount);
  const warehousesCount = formatCount(allCounts.warehouseCount);
  const zonesCount = formatCount(allCounts.zoneCount);
  const binsCount = formatCount(allCounts.binCount);
  const costCentersCount = formatCount(allCounts.costCenterCount);
  const parameterValuesCount = formatCount(allCounts.parameterCount);

  return {
    ordersCount,
    orderLinesCount,
    transactionsCount,
    batchesCount,
    materialsCount,
    materialPropertiesCount,
    locationsCount,
    warehousesCount,
    zonesCount,
    binsCount,
    costCentersCount,
    parameterValuesCount,
  };
}
